import _ from "lodash";
import {FormEvent, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Pref, UserPref, useSetTenantPrefMutation} from "../../../../generated/graphql";
import {FeatureFlagContext} from "../../../../providers/FeatureFlagProvider";
import {extractSimplePref, PreferenceContext, PrefValue} from "../../../../providers/PreferenceProvider";
import {TenantPreferences} from "../../../../constants/constants";
import {DriverRequirementEnforcementLevel} from "../../enums/DriverRequirementEnforcementLevel";
import "../common/CompanySettings.css";
import {ContentProps} from "../common/components/Content";
import Section from "../common/components/Section";
import {
  ContentNumericProp,
  ContentOptionProps,
  DEFAULT_DWELL_MINUTES,
  DEFAULT_ROUTE_PLANNING_LIMIT,
  MAX_DWELL_MINUTES,
  MIN_DWELL_MINUTES,
  RoutePlanLimitMode
} from "../common/constants";
import {renderNumericTextBox, renderRadioGroupProps} from "../common/renderer";
import {
  ConsiderSkillsOption,
  DEFAULT_CONSIDER_SKILLS_ROUTE_PLANNING,
  DEFAULT_MAP_SKILL_ENFORCEMENT,
  MapSkillEnforcement,
  toConsiderSkillsOption,
  toMapSkillEnforcement
} from "../common/itinerarySkills";
import {Body, CancelButton, Container, Footer, Header, HeaderText, SaveButton} from "../common/styles";
import log from "loglevel";
import {OptimizationObjective} from "../../enums/RoutePlanning";
import {useAuthState} from "../../../../providers/AuthProvider";

// DP-3239: the optimization settings, moved out of General into their own Company Settings page.
const Optimization = () => {
  const {tenantPreferences, tenantPrefsQueryRefetch} = useContext(PreferenceContext);
  const {isSuperUser} = useAuthState();
  const [setTenantPref, {loading: isSetTenantPrefLoading, data: setTenantPrefData}] = useSetTenantPrefMutation();
  const {routePlanning: routePlanningEnabled, itineraryPlanningSkills: itineraryPlanningSkillsEnabled} =
    useContext(FeatureFlagContext);

  const [isDispatcherIgnoreTimeWindows, setDispatcherIgnoreTimeWindows] = useState<boolean>(false);
  const [initDispatcherIgnoreTimeWindows, setInitDispatcherIgnoreTimeWindows] = useState<boolean>(false);

  const [isDispatcherIgnoreTimeWindowsOnFirstTry, setDispatcherIgnoreTimeWindowsOnFirstTry] = useState<boolean>(true);
  const [initDispatcherIgnoreTimeWindowsOnFirstTry, setInitDispatcherIgnoreTimeWindowsOnFirstTry] =
    useState<boolean>(false);

  // DP-3187: default Yes, so both start true or the page loads dirty (DP-3166)
  const [isDefineFirstStopAfterPickup, setDefineFirstStopAfterPickup] = useState<boolean>(true);
  const [initDefineFirstStopAfterPickup, setInitDefineFirstStopAfterPickup] = useState<boolean>(true);

  const [isDispatcherIgnoreTimeWindowsAsDefault, setDispatcherIgnoreTimeWindowsAsDefault] = useState<boolean>(false);
  const [initDispatcherIgnoreTimeWindowsAsDefault, setInitDispatcherIgnoreTimeWindowsAsDefault] =
    useState<boolean>(false);

  const [driverIgnoreTimeWindows, setDriverIgnoreTimeWindows] = useState<boolean>(false);
  const [initDriverIgnoreTimeWindows, setInitDriverIgnoreTimeWindows] = useState<boolean>(false);

  const [assumedOptimizationDwellTime, setAssumedOptimizationDwellTime] = useState<number | undefined>(undefined);
  const [initAssumedOptimizationDwellTime, setInitAssumedOptimizationDwellTime] = useState<number | undefined>(
    undefined
  );

  // DP-3166: unset companies default to "Yes" — both initials must match so the form is not dirty on load
  const [considerSkillsRoutePlanning, setConsiderSkillsRoutePlanning] = useState<boolean>(
    DEFAULT_CONSIDER_SKILLS_ROUTE_PLANNING
  );
  const [initConsiderSkillsRoutePlanning, setInitConsiderSkillsRoutePlanning] = useState<boolean>(
    DEFAULT_CONSIDER_SKILLS_ROUTE_PLANNING
  );
  // DP-3230: what the IP map does when a job is moved onto a driver who is missing a skill ("Yes" = Prevent)
  const [mapSkillEnforcement, setMapSkillEnforcement] = useState<MapSkillEnforcement>(DEFAULT_MAP_SKILL_ENFORCEMENT);
  const [initMapSkillEnforcement, setInitMapSkillEnforcement] =
    useState<MapSkillEnforcement>(DEFAULT_MAP_SKILL_ENFORCEMENT);

  // DP-3166: unset companies default to "Yes" — both initials must match so the form is not dirty on load
  const [dismissFailedJobsRoutePlanning, setDismissFailedJobsRoutePlanning] = useState<boolean>(true);
  const [initDismissFailedJobsRoutePlanning, setInitDismissFailedJobsRoutePlanning] = useState<boolean>(true);

  // DP-3113: default the "Optimization objectives" setting to "Both" for companies that never set it.
  const [optimizationObjectives, setOptimizationObjectives] = useState<OptimizationObjective>(
    OptimizationObjective.Both
  );
  const [initOptimizationObjectives, setInitOptimizationObjectives] = useState<OptimizationObjective>(
    OptimizationObjective.Both
  );

  const [routePlanLimitMode, setRoutePlanLimitMode] = useState<RoutePlanLimitMode>(RoutePlanLimitMode.Limited);
  const [initRoutePlanLimitMode, setInitRoutePlanLimitMode] = useState<RoutePlanLimitMode>(RoutePlanLimitMode.Limited);
  const [routePlanningLimit, setRoutePlanningLimit] = useState<number | undefined>(DEFAULT_ROUTE_PLANNING_LIMIT);
  const [initRoutePlanningLimit, setInitRoutePlanningLimit] = useState<number | undefined>(
    DEFAULT_ROUTE_PLANNING_LIMIT
  );

  // Resolves whether the write succeeded, for saves that must happen in order. reloadPrefs = false leaves the reload to
  // a later write of the same save.
  const saveTenantPref = useCallback(
    (key: string, value: any, reloadPrefs = true): Promise<boolean> =>
      setTenantPref({
        variables: {
          name: key,
          input: {
            value: JSON.stringify({value: value})
          }
        }
      })
        .then(() => {
          if (reloadPrefs && tenantPrefsQueryRefetch) tenantPrefsQueryRefetch();
          return true;
        })
        .catch((error: Error) => {
          log.error(error);
          return false;
        }),
    [setTenantPref, tenantPrefsQueryRefetch]
  );

  const updateTenantPrefs = useCallback(
    (key: string, value: any) => {
      void saveTenantPref(key, value);
    },
    [saveTenantPref]
  );

  // OnChange Handlers
  const handleConsiderSkillsRoutePlanningChange = useCallback(
    (event: FormEvent<HTMLInputElement>): void => {
      const option = event.currentTarget.value as ConsiderSkillsOption;
      setConsiderSkillsRoutePlanning(option !== ConsiderSkillsOption.No);
      if (option === ConsiderSkillsOption.YesAllowWithConfirmation) {
        setMapSkillEnforcement(DriverRequirementEnforcementLevel.Warning);
      } else if (option === ConsiderSkillsOption.Yes) {
        setMapSkillEnforcement(DriverRequirementEnforcementLevel.Prevent);
      } else {
        // "No" ignores the map mode, so keep the saved one instead of marking it dirty.
        setMapSkillEnforcement(initMapSkillEnforcement);
      }
    },
    [initMapSkillEnforcement]
  );

  const handleDispatcherIgnoreTimeWindowsChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setDispatcherIgnoreTimeWindows(selectedValue);
  }, []);

  const handleDispatcherIgnoreTimeWindowsOnFirstTryChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setDispatcherIgnoreTimeWindowsOnFirstTry(selectedValue);
  }, []);

  const handleDefineFirstStopAfterPickupChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    setDefineFirstStopAfterPickup(Boolean(JSON.parse(event.currentTarget.value)));
  }, []);

  const handleDispatcherIgnoreTimeWindowsAsDefaultChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setDispatcherIgnoreTimeWindowsAsDefault(selectedValue);
  }, []);

  const handleDriverIgnoreTimeWindowsChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setDriverIgnoreTimeWindows(selectedValue);
  }, []);

  const handleAssumedOptimizationDwellTimeChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    let selectedValue: number | undefined = parseInt(event.currentTarget.value);
    if (isNaN(selectedValue) || selectedValue < 0) {
      selectedValue = 0;
    }
    if (selectedValue > MAX_DWELL_MINUTES) {
      return;
    }
    setAssumedOptimizationDwellTime(selectedValue);
  }, []);

  const handleAssumedOptimizationDwellTimeBlur = useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      if (Number((event.target as HTMLInputElement).value) < MIN_DWELL_MINUTES) {
        handleAssumedOptimizationDwellTimeChange({
          currentTarget: {value: MIN_DWELL_MINUTES.toString()} as HTMLInputElement
        } as FormEvent<HTMLInputElement>);
      }
    },
    [handleAssumedOptimizationDwellTimeChange]
  );

  const handleDismissFailedJobsRoutePlanningChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    const selectedValue = Boolean(JSON.parse(event.currentTarget.value));
    setDismissFailedJobsRoutePlanning(selectedValue);
  }, []);

  const handleOptimizationObjectivesChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    setOptimizationObjectives(event.currentTarget.value as OptimizationObjective);
  }, []);

  const handleRoutePlanLimitModeChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    setRoutePlanLimitMode(event.currentTarget.value as RoutePlanLimitMode);
  }, []);

  const handleRoutePlanningLimitChange = useCallback((event: FormEvent<HTMLInputElement>): void => {
    // Floor at 0 (a Limited cap of 0 = no plans allowed); negatives/blanks reset to 0.
    let selectedValue: number | undefined = parseInt(event.currentTarget.value);
    if (isNaN(selectedValue) || selectedValue < 0) {
      selectedValue = 0;
    }
    setRoutePlanningLimit(selectedValue);
  }, []);

  //Dirty Checks
  const isConsiderSkillsRoutePlanningDirty = useCallback(() => {
    return initConsiderSkillsRoutePlanning !== considerSkillsRoutePlanning;
  }, [initConsiderSkillsRoutePlanning, considerSkillsRoutePlanning]);

  const isMapSkillEnforcementDirty = useCallback(() => {
    return initMapSkillEnforcement !== mapSkillEnforcement;
  }, [initMapSkillEnforcement, mapSkillEnforcement]);

  const isDispatcherIgnoreTimeWindowsDirty = useCallback(() => {
    return initDispatcherIgnoreTimeWindows !== isDispatcherIgnoreTimeWindows;
  }, [initDispatcherIgnoreTimeWindows, isDispatcherIgnoreTimeWindows]);

  const isDispatcherIgnoreTimeWindowsOnFirstTryDirty = useCallback(() => {
    return initDispatcherIgnoreTimeWindowsOnFirstTry !== isDispatcherIgnoreTimeWindowsOnFirstTry;
  }, [initDispatcherIgnoreTimeWindowsOnFirstTry, isDispatcherIgnoreTimeWindowsOnFirstTry]);

  const isDefineFirstStopAfterPickupDirty = useCallback(() => {
    return initDefineFirstStopAfterPickup !== isDefineFirstStopAfterPickup;
  }, [initDefineFirstStopAfterPickup, isDefineFirstStopAfterPickup]);

  const isDispatcherIgnoreTimeWindowsAsDefaultDirty = useCallback(() => {
    return initDispatcherIgnoreTimeWindowsAsDefault !== isDispatcherIgnoreTimeWindowsAsDefault;
  }, [initDispatcherIgnoreTimeWindowsAsDefault, isDispatcherIgnoreTimeWindowsAsDefault]);

  const isDriverIgnoreTimeWindowsDirty = useCallback(() => {
    return initDriverIgnoreTimeWindows !== driverIgnoreTimeWindows;
  }, [initDriverIgnoreTimeWindows, driverIgnoreTimeWindows]);

  const isDriverAverageDwellTimeDirty = useCallback(() => {
    return initAssumedOptimizationDwellTime !== assumedOptimizationDwellTime;
  }, [initAssumedOptimizationDwellTime, assumedOptimizationDwellTime]);

  const isDismissFailedJobsRoutePlanningDirty = useCallback(() => {
    return initDismissFailedJobsRoutePlanning !== dismissFailedJobsRoutePlanning;
  }, [initDismissFailedJobsRoutePlanning, dismissFailedJobsRoutePlanning]);

  const isOptimizationObjectivesDirty = useCallback(() => {
    return optimizationObjectives !== initOptimizationObjectives;
  }, [optimizationObjectives, initOptimizationObjectives]);

  const isRoutePlanLimitDirty = useCallback(() => {
    if (initRoutePlanLimitMode !== routePlanLimitMode) {
      return true;
    }
    if (routePlanLimitMode === RoutePlanLimitMode.Limited) {
      return initRoutePlanningLimit !== routePlanningLimit;
    }
    return false;
  }, [initRoutePlanLimitMode, routePlanLimitMode, initRoutePlanningLimit, routePlanningLimit]);

  const isSaveChange = () => {
    return (
      isDispatcherIgnoreTimeWindowsDirty() ||
      isDispatcherIgnoreTimeWindowsOnFirstTryDirty() ||
      isDefineFirstStopAfterPickupDirty() ||
      isDispatcherIgnoreTimeWindowsAsDefaultDirty() ||
      isDriverIgnoreTimeWindowsDirty() ||
      isDriverAverageDwellTimeDirty() ||
      isConsiderSkillsRoutePlanningDirty() ||
      isMapSkillEnforcementDirty() ||
      isDismissFailedJobsRoutePlanningDirty() ||
      isOptimizationObjectivesDirty() ||
      isRoutePlanLimitDirty()
    );
  };

  const handleSaveSetting = useCallback(() => {
    if (isDispatcherIgnoreTimeWindowsDirty()) {
      updateTenantPrefs(TenantPreferences.dispatcherIgnoreTimeWindows, isDispatcherIgnoreTimeWindows);
    }
    if (isDispatcherIgnoreTimeWindowsOnFirstTryDirty()) {
      updateTenantPrefs(
        TenantPreferences.dispatcherIgnoreTimeWindowsOnFirstTry,
        isDispatcherIgnoreTimeWindowsOnFirstTry
      );
    }
    if (isDefineFirstStopAfterPickupDirty()) {
      updateTenantPrefs(TenantPreferences.defineFirstStopAfterPickup, isDefineFirstStopAfterPickup);
    }
    if (isDispatcherIgnoreTimeWindowsAsDefaultDirty()) {
      updateTenantPrefs(TenantPreferences.dispatcherIgnoreTimeWindowsAsDefault, isDispatcherIgnoreTimeWindowsAsDefault);
    }
    if (isDriverIgnoreTimeWindowsDirty()) {
      updateTenantPrefs(TenantPreferences.driverIgnoreTimeWindows, driverIgnoreTimeWindows);
      if (driverIgnoreTimeWindows && !assumedOptimizationDwellTime) {
        updateTenantPrefs(TenantPreferences.assumedOptimizationDwellTime, DEFAULT_DWELL_MINUTES);
      }
    }
    if (isDriverAverageDwellTimeDirty()) {
      updateTenantPrefs(TenantPreferences.assumedOptimizationDwellTime, assumedOptimizationDwellTime);
    }
    if (isMapSkillEnforcementDirty() || isConsiderSkillsRoutePlanningDirty()) {
      // Map mode first, one write after the other: the map mode has no effect while considerSkills is false, so a
      // failed second write leaves the effective setting as it was. Only the last write reloads the prefs: Apollo
      // shares identical in-flight queries, so a reload sent before considerSkills landed would also answer the reload
      // after it and bring back the old value.
      const writeConsiderSkills = isConsiderSkillsRoutePlanningDirty();
      const mapSkillEnforcementSaved = isMapSkillEnforcementDirty()
        ? saveTenantPref(TenantPreferences.mapSkillEnforcementRoutePlanning, mapSkillEnforcement, !writeConsiderSkills)
        : Promise.resolve(true);
      void mapSkillEnforcementSaved.then((saved) => {
        if (saved && writeConsiderSkills) {
          updateTenantPrefs(TenantPreferences.considerSkillsRoutePlanning, considerSkillsRoutePlanning);
        }
      });
    }
    if (isDismissFailedJobsRoutePlanningDirty()) {
      updateTenantPrefs(TenantPreferences.dismissFailedJobsRoutePlanning, dismissFailedJobsRoutePlanning);
    }
    if (isOptimizationObjectivesDirty()) {
      updateTenantPrefs(TenantPreferences.optimizationObjectives, optimizationObjectives);
    }
    if (isRoutePlanLimitDirty()) {
      // Stored as { isUnlimited, value }. The cap is always persisted (incl. 0 = no
      // plans) so it is remembered while Unlimited and restored when switching back.
      updateTenantPrefs(TenantPreferences.routePlanningLimit, {
        isUnlimited: routePlanLimitMode === RoutePlanLimitMode.Unlimited,
        value: routePlanningLimit ?? DEFAULT_ROUTE_PLANNING_LIMIT
      });
    }
  }, [
    isOptimizationObjectivesDirty,
    isDispatcherIgnoreTimeWindowsDirty,
    isDispatcherIgnoreTimeWindowsOnFirstTryDirty,
    isDefineFirstStopAfterPickupDirty,
    isDispatcherIgnoreTimeWindowsAsDefaultDirty,
    isDriverIgnoreTimeWindowsDirty,
    isDriverAverageDwellTimeDirty,
    isDismissFailedJobsRoutePlanningDirty,
    isRoutePlanLimitDirty,
    routePlanLimitMode,
    routePlanningLimit,
    saveTenantPref,
    updateTenantPrefs,
    optimizationObjectives,
    isDispatcherIgnoreTimeWindows,
    isDispatcherIgnoreTimeWindowsOnFirstTry,
    isDefineFirstStopAfterPickup,
    isDispatcherIgnoreTimeWindowsAsDefault,
    driverIgnoreTimeWindows,
    assumedOptimizationDwellTime,
    isConsiderSkillsRoutePlanningDirty,
    considerSkillsRoutePlanning,
    isMapSkillEnforcementDirty,
    mapSkillEnforcement,
    dismissFailedJobsRoutePlanning
  ]);

  const handleCancelSetting = useCallback(() => {
    setDispatcherIgnoreTimeWindows(initDispatcherIgnoreTimeWindows);
    setDispatcherIgnoreTimeWindowsOnFirstTry(initDispatcherIgnoreTimeWindowsOnFirstTry);
    setDefineFirstStopAfterPickup(initDefineFirstStopAfterPickup);
    setDispatcherIgnoreTimeWindowsAsDefault(initDispatcherIgnoreTimeWindowsAsDefault);
    setDriverIgnoreTimeWindows(initDriverIgnoreTimeWindows);
    setAssumedOptimizationDwellTime(initAssumedOptimizationDwellTime);
    setConsiderSkillsRoutePlanning(initConsiderSkillsRoutePlanning);
    setMapSkillEnforcement(initMapSkillEnforcement);
    setDismissFailedJobsRoutePlanning(initDismissFailedJobsRoutePlanning);
    setOptimizationObjectives(initOptimizationObjectives);
    setRoutePlanLimitMode(initRoutePlanLimitMode);
    setRoutePlanningLimit(initRoutePlanningLimit);
  }, [
    initDispatcherIgnoreTimeWindows,
    initDispatcherIgnoreTimeWindowsOnFirstTry,
    initDefineFirstStopAfterPickup,
    initDispatcherIgnoreTimeWindowsAsDefault,
    initDriverIgnoreTimeWindows,
    initAssumedOptimizationDwellTime,
    initConsiderSkillsRoutePlanning,
    initMapSkillEnforcement,
    initDismissFailedJobsRoutePlanning,
    initOptimizationObjectives,
    initRoutePlanLimitMode,
    initRoutePlanningLimit
  ]);

  const renderItineraryPlanningSettings = useMemo(() => {
    const retVal: ContentProps[] = [];

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Allow Dispatcher to dismiss failed jobs in Itinerary Planning",
      contentInformation: "Allow dispatchers to dismiss failed jobs in Itinerary Planning.",
      contentOptions: [
        {
          group: "dismissFailedJobsRoutePlanning",
          label: "Yes",
          value: "true"
        },
        {
          group: "dismissFailedJobsRoutePlanning",
          label: "No",
          value: "false"
        }
      ],
      handleChangeOption: handleDismissFailedJobsRoutePlanningChange,
      renderPropComponents: renderRadioGroupProps,
      selectedOption: _.toString(dismissFailedJobsRoutePlanning)
    });

    if (itineraryPlanningSkillsEnabled) {
      retVal.push({
        ...ContentOptionProps,
        contentTitle: "Consider Skills in Itinerary Planning",
        contentInformation:
          "Yes: the route calculation only assigns jobs to drivers who have all necessary skills, and on the map a " +
          "job cannot be moved to a driver who is missing one. Yes – Allow with Confirmation on Map: the route " +
          "calculation still considers skills; on the map, moving a job to a driver who is missing a skill asks for " +
          "confirmation and a reason. No: skills are not considered.",
        contentOptions: [
          {
            group: "considerSkillsRoutePlanning",
            label: "Yes",
            value: ConsiderSkillsOption.Yes
          },
          {
            group: "considerSkillsRoutePlanning",
            label: "Yes – Allow with Confirmation on Map",
            value: ConsiderSkillsOption.YesAllowWithConfirmation
          },
          {
            group: "considerSkillsRoutePlanning",
            label: "No",
            value: ConsiderSkillsOption.No
          }
        ],
        selectedOption: toConsiderSkillsOption(considerSkillsRoutePlanning, mapSkillEnforcement),
        handleChangeOption: handleConsiderSkillsRoutePlanningChange,
        renderPropComponents: renderRadioGroupProps
      });
    }

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Optimization objectives available to the dispatcher",
      contentOptions: [
        {
          group: "optimizationObjectives",
          label: "Minimize time to complete the work",
          value: OptimizationObjective.MinCompletionTime
        },
        {
          group: "optimizationObjectives",
          label: "Divide work as evenly as possible based on time working",
          value: OptimizationObjective.BalanceWorks
        },
        {
          group: "optimizationObjectives",
          label: "Both",
          value: OptimizationObjective.Both
        }
      ],
      contentInformation:
        "If 'Both' is selected, these options will be available for the Dispatcher in the Itinerary Planning flow. If one is selected, it will assume this is the goal.",
      selectedOption: optimizationObjectives,
      handleChangeOption: handleOptimizationObjectivesChange,
      renderPropComponents: renderRadioGroupProps
    });

    // Freemium weekly itinerary-plan limit (DP-3062) — internal e-Courier admins only.
    if (isSuperUser) {
      retVal.push({
        ...ContentOptionProps,
        contentTitle: "Itinerary Plan Weekly Limit",
        contentInformation:
          "Limit how many itinerary plans this company can run per week (Mon–Sun, resets 12:01 AM EST/EDT). Internal admins only.",
        contentOptions: [
          {
            group: "routePlanLimitMode",
            label: "Unlimited",
            value: RoutePlanLimitMode.Unlimited
          },
          {
            group: "routePlanLimitMode",
            label: "Limited",
            value: RoutePlanLimitMode.Limited
          }
        ],
        selectedOption: routePlanLimitMode,
        handleChangeOption: handleRoutePlanLimitModeChange,
        renderPropComponents: renderRadioGroupProps
      });

      retVal.push({
        ...ContentNumericProp,
        contentTitle: "Plans per week",
        contentInformation: (
          <div>
            Maximum number of itinerary plans this company can run each week.
            <br />
            Set to 0 to block all itinerary planning.
          </div>
        ),
        contentUnit: "plans",
        selectedOption: _.toString(routePlanningLimit),
        handleChangeOption: handleRoutePlanningLimitChange,
        renderPropComponents: renderNumericTextBox,
        isDisabled: () => routePlanLimitMode !== RoutePlanLimitMode.Limited
      });
    }

    return retVal;
  }, [
    handleDismissFailedJobsRoutePlanningChange,
    dismissFailedJobsRoutePlanning,
    itineraryPlanningSkillsEnabled,
    considerSkillsRoutePlanning,
    mapSkillEnforcement,
    handleConsiderSkillsRoutePlanningChange,
    optimizationObjectives,
    handleOptimizationObjectivesChange,
    isSuperUser,
    routePlanLimitMode,
    handleRoutePlanLimitModeChange,
    routePlanningLimit,
    handleRoutePlanningLimitChange
  ]);

  const renderSingleManifestOptimizationSettings = useMemo(() => {
    const retVal: ContentProps[] = [];

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Allow Dispatchers to Ignore Optimization Time Window",
      contentOptions: [
        {
          group: "dispatcher-timewindow",
          label: "Yes",
          value: "true"
        },
        {
          group: "dispatcher-timewindow",
          label: "No",
          value: "false"
        }
      ],
      contentInformation:
        "Allow dispatchers to execute route optimizations while ignoring time windows determined by stops.",
      selectedOption: _.toString(isDispatcherIgnoreTimeWindows),
      handleChangeOption: handleDispatcherIgnoreTimeWindowsChange,
      renderPropComponents: renderRadioGroupProps
    });

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Allow dispatchers to ignore time windows on first try",
      contentOptions: [
        {
          group: "dispatcher-timewindow-on-firsttry",
          label: "Yes",
          value: "true"
        },
        {
          group: "dispatcher-timewindow-on-firsttry",
          label: "No",
          value: "false"
        }
      ],
      contentInformation: "Allow dispatchers to ignore time windows on first try when executing route optimizations.",
      selectedOption: _.toString(isDispatcherIgnoreTimeWindowsOnFirstTry),
      handleChangeOption: handleDispatcherIgnoreTimeWindowsOnFirstTryChange,
      renderPropComponents: renderRadioGroupProps,
      isDisabled: () => !isDispatcherIgnoreTimeWindows
    });

    retVal.push({
      ...ContentOptionProps,
      contentTitle: "Ignore time windows as default",
      contentOptions: [
        {
          group: "dispatcher-timewindow-as-default",
          label: "Yes",
          value: "true"
        },
        {
          group: "dispatcher-timewindow-as-default",
          label: "No",
          value: "false"
        }
      ],
      contentInformation:
        "Allow dispatchers to ignore time windows on first try by default when executing route optimizations.",
      selectedOption: _.toString(isDispatcherIgnoreTimeWindowsAsDefault),
      handleChangeOption: handleDispatcherIgnoreTimeWindowsAsDefaultChange,
      renderPropComponents: renderRadioGroupProps,
      isDisabled: () => !isDispatcherIgnoreTimeWindows || !isDispatcherIgnoreTimeWindowsOnFirstTry
    });

    retVal.push(
      {
        ...ContentOptionProps,
        contentTitle: "Allow Drivers to Ignore Optimization Time Window",
        contentOptions: [
          {
            group: "driver-timewindow",
            label: "Yes",
            value: "true"
          },
          {
            group: "driver-timewindow",
            label: "No",
            value: "false"
          }
        ],
        contentInformation:
          "Allow drivers to execute route optimizations while ignoring time windows determined by stops.",
        selectedOption: _.toString(driverIgnoreTimeWindows),
        handleChangeOption: handleDriverIgnoreTimeWindowsChange,
        renderPropComponents: renderRadioGroupProps
      },
      {
        ...ContentOptionProps,
        contentTitle: "Define first stop after pickup in optimization",
        contentOptions: [
          {
            group: "define-first-stop-after-pickup",
            label: "Yes",
            value: "true"
          },
          {
            group: "define-first-stop-after-pickup",
            label: "No",
            value: "false"
          }
        ],
        contentInformation:
          "When a manifest optimization starts at a pickup stop, let dispatchers choose the stop the driver visits right after the pickups at that location.",
        selectedOption: _.toString(isDefineFirstStopAfterPickup),
        handleChangeOption: handleDefineFirstStopAfterPickupChange,
        renderPropComponents: renderRadioGroupProps
      },
      {
        ...ContentNumericProp,
        contentTitle: "Assumed Optimization Dwell Time (Also Impacts Itinerary Planning)",
        contentInformation: (
          <div>
            Assumed Optimization Dwell Time per stop.
            <br />
            Minimum: {MIN_DWELL_MINUTES} minute, Maximum: {MAX_DWELL_MINUTES} minutes.
          </div>
        ),
        contentUnit: "minutes",
        selectedOption: _.toString(assumedOptimizationDwellTime),
        handleChangeOption: handleAssumedOptimizationDwellTimeChange,
        handleBlur: handleAssumedOptimizationDwellTimeBlur,
        renderPropComponents: renderNumericTextBox
      }
    );

    return retVal;
  }, [
    isDispatcherIgnoreTimeWindows,
    handleDispatcherIgnoreTimeWindowsChange,
    isDispatcherIgnoreTimeWindowsOnFirstTry,
    handleDispatcherIgnoreTimeWindowsOnFirstTryChange,
    isDefineFirstStopAfterPickup,
    handleDefineFirstStopAfterPickupChange,
    isDispatcherIgnoreTimeWindowsAsDefault,
    handleDispatcherIgnoreTimeWindowsAsDefaultChange,
    driverIgnoreTimeWindows,
    handleDriverIgnoreTimeWindowsChange,
    assumedOptimizationDwellTime,
    handleAssumedOptimizationDwellTimeChange,
    handleAssumedOptimizationDwellTimeBlur
  ]);

  const handleConsiderSkillsRoutePlanning = useCallback((prefs: Pref[]) => {
    // DP-3166: unset companies default to "Yes"
    const prefVal: PrefValue<boolean> = extractSimplePref(
      prefs,
      TenantPreferences.considerSkillsRoutePlanning,
      DEFAULT_CONSIDER_SKILLS_ROUTE_PLANNING
    );
    setConsiderSkillsRoutePlanning(prefVal.value as boolean);
    setInitConsiderSkillsRoutePlanning(prefVal.value as boolean);
  }, []);

  const handleMapSkillEnforcement = useCallback((prefs: Pref[]) => {
    const prefVal = extractSimplePref(
      prefs,
      TenantPreferences.mapSkillEnforcementRoutePlanning,
      DEFAULT_MAP_SKILL_ENFORCEMENT
    );
    const value = toMapSkillEnforcement(prefVal.value);
    setMapSkillEnforcement(value);
    setInitMapSkillEnforcement(value);
  }, []);

  const handleDispatcherIgnoreTimeWindows = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.dispatcherIgnoreTimeWindows, false);
    setDispatcherIgnoreTimeWindows(prefVal.value as boolean);
    setInitDispatcherIgnoreTimeWindows(prefVal.value as boolean);
  }, []);

  const handleDispatcherIgnoreTimeWindowsOnFirstTry = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(
      prefs,
      TenantPreferences.dispatcherIgnoreTimeWindowsOnFirstTry,
      true
    );
    setDispatcherIgnoreTimeWindowsOnFirstTry(prefVal.value as boolean);
    setInitDispatcherIgnoreTimeWindowsOnFirstTry(prefVal.value as boolean);
  }, []);

  const handleDefineFirstStopAfterPickup = useCallback((prefs: Pref[]) => {
    // DP-3187: companies that never saved the setting get Yes
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.defineFirstStopAfterPickup, true);
    setDefineFirstStopAfterPickup(prefVal.value as boolean);
    setInitDefineFirstStopAfterPickup(prefVal.value as boolean);
  }, []);

  const handleDispatcherIgnoreTimeWindowsAsDefault = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(
      prefs,
      TenantPreferences.dispatcherIgnoreTimeWindowsAsDefault,
      false
    );
    setDispatcherIgnoreTimeWindowsAsDefault(prefVal.value as boolean);
    setInitDispatcherIgnoreTimeWindowsAsDefault(prefVal.value as boolean);
  }, []);

  const handleDriverIgnoreTimeWindows = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(prefs, TenantPreferences.driverIgnoreTimeWindows, false);
    setDriverIgnoreTimeWindows(prefVal.value as boolean);
    setInitDriverIgnoreTimeWindows(prefVal.value as boolean);
  }, []);

  const handleAssumedOptimizationDwellTime = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<number> = extractSimplePref(
      prefs,
      TenantPreferences.assumedOptimizationDwellTime,
      DEFAULT_DWELL_MINUTES
    );
    const value = Math.min(prefVal.value as number, MAX_DWELL_MINUTES);
    setAssumedOptimizationDwellTime(value);
    setInitAssumedOptimizationDwellTime(value);
  }, []);

  const handleDismissFailedJobsRoutePlanning = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<boolean> = extractSimplePref(
      prefs,
      TenantPreferences.dismissFailedJobsRoutePlanning,
      true // DP-3166: unset companies default to "Yes"
    );
    setDismissFailedJobsRoutePlanning(prefVal.value as boolean);
    setInitDismissFailedJobsRoutePlanning(prefVal.value as boolean);
  }, []);

  const handleOptimizationObjectives = useCallback((prefs: Pref[]) => {
    const prefVal: PrefValue<OptimizationObjective> = extractSimplePref(
      prefs,
      TenantPreferences.optimizationObjectives,
      OptimizationObjective.Both // DP-3113: unset companies default to "Both"
    );
    setOptimizationObjectives(prefVal.value as OptimizationObjective);
    setInitOptimizationObjectives(prefVal.value as OptimizationObjective);
  }, []);

  const handleRoutePlanningLimit = useCallback((prefs: Pref[]) => {
    // Stored as { isUnlimited, value }. Unlimited is the explicit flag; the cap is
    // always loaded (defaulting to Limited 2 when absent) so it is shown/remembered
    // regardless of mode. 0 is a valid cap (no plans).
    const pref = extractSimplePref<{isUnlimited?: boolean; value?: number}>(
      prefs,
      TenantPreferences.routePlanningLimit,
      {
        isUnlimited: false,
        value: DEFAULT_ROUTE_PLANNING_LIMIT
      }
    ).value;
    const capValue = Number(pref?.value);
    const cap = Number.isFinite(capValue) && capValue >= 0 ? capValue : DEFAULT_ROUTE_PLANNING_LIMIT;
    const mode = pref?.isUnlimited ? RoutePlanLimitMode.Unlimited : RoutePlanLimitMode.Limited;

    setRoutePlanLimitMode(mode);
    setInitRoutePlanLimitMode(mode);
    setRoutePlanningLimit(cap);
    setInitRoutePlanningLimit(cap);
  }, []);

  useEffect(() => {
    log.debug("Tenant preferences loaded: ", tenantPreferences);
    handleDispatcherIgnoreTimeWindows(tenantPreferences);
    handleDispatcherIgnoreTimeWindowsOnFirstTry(tenantPreferences);
    handleDefineFirstStopAfterPickup(tenantPreferences);
    handleDispatcherIgnoreTimeWindowsAsDefault(tenantPreferences);
    handleDriverIgnoreTimeWindows(tenantPreferences);
    handleAssumedOptimizationDwellTime(tenantPreferences);
    handleConsiderSkillsRoutePlanning(tenantPreferences);
    handleMapSkillEnforcement(tenantPreferences);
    handleDismissFailedJobsRoutePlanning(tenantPreferences);
    handleOptimizationObjectives(tenantPreferences);
    handleRoutePlanningLimit(tenantPreferences);
  }, [
    tenantPreferences,
    handleDispatcherIgnoreTimeWindows,
    handleDispatcherIgnoreTimeWindowsOnFirstTry,
    handleDefineFirstStopAfterPickup,
    handleDispatcherIgnoreTimeWindowsAsDefault,
    handleDriverIgnoreTimeWindows,
    handleAssumedOptimizationDwellTime,
    handleConsiderSkillsRoutePlanning,
    handleMapSkillEnforcement,
    handleDismissFailedJobsRoutePlanning,
    handleOptimizationObjectives,
    handleRoutePlanningLimit
  ]);

  useEffect(() => {
    const data = setTenantPrefData?.setTenantPref as UserPref;
    log.debug("Pref saved: ", data);
    if (data && data.name === TenantPreferences.dispatcherIgnoreTimeWindows) {
      handleDispatcherIgnoreTimeWindows([data]);
    }
    if (data && data.name === TenantPreferences.dispatcherIgnoreTimeWindowsOnFirstTry) {
      handleDispatcherIgnoreTimeWindowsOnFirstTry([data]);
    }
    if (data && data.name === TenantPreferences.defineFirstStopAfterPickup) {
      handleDefineFirstStopAfterPickup([data]);
    }
    if (data && data.name === TenantPreferences.dispatcherIgnoreTimeWindowsAsDefault) {
      handleDispatcherIgnoreTimeWindowsAsDefault([data]);
    }
    if (data && data.name === TenantPreferences.driverIgnoreTimeWindows) {
      handleDriverIgnoreTimeWindows([data]);
    }
    if (data && data.name === TenantPreferences.assumedOptimizationDwellTime) {
      handleAssumedOptimizationDwellTime([data]);
    }
    if (data && data.name === TenantPreferences.considerSkillsRoutePlanning) {
      handleConsiderSkillsRoutePlanning([data]);
    }
    if (data && data.name === TenantPreferences.mapSkillEnforcementRoutePlanning) {
      handleMapSkillEnforcement([data]);
    }
    if (data && data.name === TenantPreferences.dismissFailedJobsRoutePlanning) {
      handleDismissFailedJobsRoutePlanning([data]);
    }
    if (data && data.name === TenantPreferences.optimizationObjectives) {
      handleOptimizationObjectives([data]);
    }
    if (data && data.name === TenantPreferences.routePlanningLimit) {
      handleRoutePlanningLimit([data]);
    }
  }, [
    setTenantPrefData?.setTenantPref,
    handleDispatcherIgnoreTimeWindows,
    handleDispatcherIgnoreTimeWindowsOnFirstTry,
    handleDefineFirstStopAfterPickup,
    handleDispatcherIgnoreTimeWindowsAsDefault,
    handleDriverIgnoreTimeWindows,
    handleAssumedOptimizationDwellTime,
    handleConsiderSkillsRoutePlanning,
    handleMapSkillEnforcement,
    handleDismissFailedJobsRoutePlanning,
    handleOptimizationObjectives,
    handleRoutePlanningLimit
  ]);

  return (
    <Container>
      <Header>
        <HeaderText>Company Settings</HeaderText>
      </Header>
      <Body>
        {/* Every Itinerary Planning row needs the routePlanning flag; hide the header too rather than show it empty. */}
        {routePlanningEnabled && (
          <Section
            sectionName="itinerary-planning-section"
            sectionTitle="Itinerary Planning"
            render={renderItineraryPlanningSettings}
          />
        )}
        <Section
          sectionName="single-manifest-optimization-section"
          sectionTitle="Single-Manifest Optimization"
          render={renderSingleManifestOptimizationSettings}
        />
      </Body>
      <Footer>
        <CancelButton
          data-testid="cancel-button"
          disabled={!isSaveChange() || isSetTenantPrefLoading}
          onClick={handleCancelSetting}
          intent="none"
          outlined
        >
          Cancel
        </CancelButton>
        <SaveButton
          data-testid="save-button"
          disabled={!isSaveChange() || isSetTenantPrefLoading}
          onClick={handleSaveSetting}
          intent="primary"
        >
          Save
        </SaveButton>
      </Footer>
    </Container>
  );
};

export default Optimization;
